/* Existing effects intentionally preserve lifecycle semantics. */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSafePostLoginPath } from './authCallbackSecurity';
import { API_URL } from '@/utils/apiUrl';


const safeStorageWrite = (key, value) => {
  let success = false;

  try {
    localStorage.setItem(key, value);
    success = localStorage.getItem(key) === value;
  } catch {
    // sessionStorage may still be available.
  }

  try {
    sessionStorage.setItem(key, value);
    success = sessionStorage.getItem(key) === value || success;
  } catch {
    // localStorage may already contain the value.
  }

  return success;
};

const storageContains = (key, value) => {
  try {
    if (localStorage.getItem(key) === value) return true;
  } catch {
    // Try sessionStorage below.
  }

  try {
    return sessionStorage.getItem(key) === value;
  } catch {
    return false;
  }
};

const isMobile = () => (
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
);

const isSafari = () => (
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
);

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  const nextPage = getSafePostLoginPath(searchParams.get('next'));

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const sessionId = hashParams.get('session_id') || searchParams.get('session_id');

        // Remove one-time credentials and untrusted query values before any network request.
        window.history.replaceState(null, '', window.location.pathname);

        if (!sessionId) {
          setError('Geçerli bir oturum isteği bulunamadı');
          setStatus('error');
          setTimeout(() => navigate('/', { replace: true }), 2000);
          return;
        }

        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          throw new Error('Oturum doğrulanamadı');
        }

        const data = await response.json();
        if (
          typeof data.session_token !== 'string'
          || data.session_token.length === 0
          || !data.user
          || typeof data.user !== 'object'
          || Array.isArray(data.user)
        ) {
          throw new Error('Oturum yanıtı geçersiz');
        }

        const serializedUser = JSON.stringify(data.user);
        const authTimestamp = Date.now().toString();

        const attemptStorage = () => {
          const tokenStored = safeStorageWrite('session_token', data.session_token);
          const userStored = safeStorageWrite('user', serializedUser);
          const loginStored = safeStorageWrite('isLoggedIn', 'true');
          const timestampStored = safeStorageWrite('auth_timestamp', authTimestamp);
          const emailStored = data.user.email
            ? safeStorageWrite('user_email', data.user.email)
            : true;

          return tokenStored && userStored && loginStored && timestampStored && emailStored;
        };

        let storageSuccess = attemptStorage();

        if (!storageSuccess && (isMobile() || isSafari())) {
          await new Promise(resolve => setTimeout(resolve, 200));
          storageSuccess = attemptStorage();

          if (!storageSuccess) {
            await new Promise(resolve => setTimeout(resolve, 500));
            storageSuccess = attemptStorage();
          }
        }

        const storageVerified = storageSuccess
          && storageContains('session_token', data.session_token)
          && storageContains('user', serializedUser)
          && storageContains('isLoggedIn', 'true');

        if (!storageVerified) {
          throw new Error('Oturum tarayıcıda güvenli biçimde saklanamadı');
        }

        setStatus('success');

        const navigateDelay = isMobile() ? 800 : 300;
        setTimeout(() => {
          navigate(nextPage, { replace: true });
        }, navigateDelay);
      } catch {
        setError('Giriş tamamlanamadı. Lütfen tekrar deneyin.');
        setStatus('error');
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    setTimeout(processAuth, 100);
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-[#060710] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {status === 'processing' && (
          <>
            <div className="w-10 h-10 border border-white/20 border-t-white rounded-full animate-spin mx-auto mb-6" />
            <p className="text-white text-base font-light">Giriş yapılıyor…</p>
            <p className="text-white/40 text-xs mt-2 font-mono uppercase tracking-wider">Lütfen bekleyin</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-10 h-10 border border-[#27C93F]/40 flex items-center justify-center mx-auto mb-6">
              <svg className="w-5 h-5 text-[#27C93F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-white text-base font-light">Giriş başarılı</p>
            <p className="text-white/40 text-xs mt-2 font-mono uppercase tracking-wider">Dashboard'a yönlendiriliyorsunuz</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-10 h-10 border border-[#FF3B30]/40 flex items-center justify-center mx-auto mb-6">
              <svg className="w-5 h-5 text-[#FF3B30]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-white text-base font-light">Giriş hatası</p>
            <p className="text-[#FF3B30] text-sm mt-2">{error}</p>
            <p className="text-white/30 text-xs mt-4 font-mono uppercase tracking-wider">Ana sayfaya yönlendiriliyorsunuz</p>
          </>
        )}
      </div>
    </div>
  );
}
